// src/components/VideoUploader.tsx
import React, { useState, ChangeEvent, useEffect } from 'react';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage } from '../../services/firebase'; // Adjust the import path as necessary

interface VideoUploaderProps {
  onUploadSuccess: (url: string) => void;
  value?: string | null;
  onDeleteSuccess?: (url: string) => void; // Optional callback for deletion
  isDefaultVideo?: boolean; // Indicates if this is a default video
}

const ACCEPTED_FORMATS = ['video/mp4', 'video/webm'];
const MAX_SIZE_MB = 15;

const VideoUploader: React.FC<VideoUploaderProps> = ({ onUploadSuccess, value, onDeleteSuccess, isDefaultVideo }) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string>('');
  const [videoURL, setVideoURL] = useState<string | null>(value || null);
  const [defaultVideoSet, setDefaultVideoSet] = useState<boolean | undefined>(isDefaultVideo || undefined);

  const handleFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setError('');

    if (!ACCEPTED_FORMATS.includes(file.type)) {
      setError('Only .mp4 and .webm formats are allowed.');
      return;
    }

    const sizeInMB = file.size / (1024 * 1024);
    if (sizeInMB > MAX_SIZE_MB) {
      setError('File size must be under 15 MB.');
      return;
    }

    const fileName = `${Date.now()}_${file.name}`;
    const storageRef = ref(storage, `videos/${fileName}`);

    setUploading(true);
    try {
      const snapshot = await uploadBytes(storageRef, file);
      const url = await getDownloadURL(snapshot.ref);
      setVideoURL(url);
      onUploadSuccess(url);
    } catch (err) {
      console.error(err);
      setError('Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  useEffect(() => {
    if (value === '/fantasyloser-default-video.webm' && isDefaultVideo){
      setVideoURL(null);
    } else {
      setVideoURL(value || null); // 
    }
    
  }, [value]);

  const deleteVideo = async (url: string) => {
    await deleteVideoFromFirebase(url);
    setVideoURL(null);
    if (onDeleteSuccess) {
      onDeleteSuccess(url); // Notify parent component that video has been cleared
    }
  };

  /**
 * Deletes a video from Firebase Storage using its download URL
 * @param videoUrl The full Firebase Storage download URL
 */
 const deleteVideoFromFirebase = async (videoUrl: string): Promise<void> => {
  try {
    // Extract path from URL (everything after `/o/` and before `?`)
    const decodedUrl = decodeURIComponent(videoUrl);
    const match = decodedUrl.match(/\/o\/(.+)\?/);

    if (!match || !match[1]) {
      throw new Error('Invalid video URL. Could not extract path.');
    }

    const fullPath = match[1]; // e.g., "videos/yourfilename.webm"
    const videoRef = ref(storage, fullPath);

    await deleteObject(videoRef);
    console.log('Video deleted successfully');
  } catch (error) {
    console.error('Failed to delete video:', error);
    throw error;
  }
};


  return (
    <div>
      <input
        type="file"
        accept=".mp4,.webm"
        onChange={handleFileChange}
        disabled={uploading}
      />
      {uploading && <p>Uploading video...</p>}
      {error && <p style={{ color: 'red' }}>{error}</p>}
      {videoURL && (
        <video width="320" height="240" controls key={videoURL}>
          <source src={videoURL} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
      )}
      {
        videoURL && !uploading && !defaultVideoSet && (
          <button type='button' onClick={()=>deleteVideo(videoURL)} className="mt-2 bg-red-500 text-white px-4 py-2 rounded">
            Clear Video
          </button>
        )
      }
    </div>
  );
};

export default VideoUploader;
